'use client';

import { useEffect, useMemo, useState } from 'react';
import { interpolateStringWithData } from 'utils';
import { c } from '../../../utils/tailwind-merge';
import { Dropdown } from '../../atoms/Dropdown';
import { NotFound } from '../../molecules/NotFound';
import { Pagination } from '../../molecules/Pagination';
import { RichText } from '../../molecules/RichText';
import { emptyWriteReview } from '../../organisms/WriteReviewModal/WriteReviewModal';
import {
  UserProductReview,
  UserProductReviewPropsData,
} from '../UserProductReview';
import { WriteReviewModal } from '../WriteReviewModal';
import {
  ProductReviewFilterValues,
  ProductReviewSortByValues,
  ProductReviewsProps,
} from './ProductReviews.types';

export const ProductReviews = ({
  activeFilterValue,
  microcopy,
  onHelpfulVotesReview,
  onEditReview,
  onHelpfulVotesReply,
  onEditReply,
  onDeleteReply,
  onSubmitReply,
  onFilter,
  onSort,
  onPageChange,
  onSubmitReport,
  onEmailPublisher,
  onSubmitReview,
  onDeleteReview,
  reviews,
  start,
  end,
  total,
  currentPage,
  totalPages,
  writeReviewModal,
  className,
}: ProductReviewsProps) => {
  if (reviews?.length && microcopy?.filterOptions?.length !== 6) {
    throw new Error(
      "filterOptions microcopies must have exactly 6 items (1 default 'Rating' option plus 5 star ratings)"
    );
  }
  if (reviews?.length && microcopy?.sortOptions?.length !== 2) {
    throw new Error('sortOptions microcopies must have exactly 2 items');
  }

  const filterOptions = useMemo(
    () => [
      {
        label: microcopy.filterOptions[0],
        value: ProductReviewFilterValues.DEFAULT,
      },
      {
        label: microcopy.filterOptions[1],
        value: ProductReviewFilterValues.ONE_STAR,
      },
      {
        label: microcopy.filterOptions[2],
        value: ProductReviewFilterValues.TWO_STARS,
      },
      {
        label: microcopy.filterOptions[3],
        value: ProductReviewFilterValues.THREE_STARS,
      },
      {
        label: microcopy.filterOptions[4],
        value: ProductReviewFilterValues.FOUR_STARS,
      },
      {
        label: microcopy.filterOptions[5],
        value: ProductReviewFilterValues.FIVE_STARS,
      },
    ],
    [microcopy.filterOptions]
  );

  const sortByOptions = [
    {
      label: microcopy.sortOptions[0],
      value: ProductReviewSortByValues.RECENT,
    },
    {
      label: microcopy.sortOptions[1],
      value: ProductReviewSortByValues.RATING,
    },
  ];

  const [activeSortBy, setActiveSortBy] = useState({
    value: sortByOptions[0].value,
    label: sortByOptions[0].label,
  });

  const [activeRating, setActiveRating] = useState({
    value: filterOptions[0].value,
    label: filterOptions[0].label,
  });

  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (
      typeof activeFilterValue !== 'undefined' &&
      activeFilterValue !== activeRating.value
    ) {
      const found = filterOptions.find(
        (opt) => opt.value === activeFilterValue
      );
      if (found) {
        setActiveRating({ value: found.value, label: found.label });
      }
    }
  }, [activeFilterValue, activeRating.value, filterOptions]);

  const renderEmptyState = () => (
    <NotFound
      title={microcopy.notFound?.title || ''}
      description={microcopy.notFound?.description || ''}
      className="text-center"
    >
      <WriteReviewModal
        title={writeReviewModal?.title || ''}
        imageUrl={writeReviewModal?.imageUrl || ''}
        version={writeReviewModal?.version || ''}
        ownedDate={writeReviewModal?.ownedDate || ''}
        isMultiPackage={writeReviewModal?.isMultiPackage || false}
        microcopy={microcopy.emptyState?.writeReviewModal || emptyWriteReview}
        onEmailPublisher={onEmailPublisher}
        onSubmitReview={onSubmitReview}
        data-test="empty-write-review-button"
      />
    </NotFound>
  );

  return (
    <section className={c(className)}>
      <h2 className="mb-6 text-md font-medium">{microcopy.reviews}</h2>
      <nav className="mb-4 block w-full justify-between md:flex">
        <div className="flex gap-4">
          <div className="w-1/2 text-sm text-grey-600">
            <Dropdown
              id="rating-dropdown"
              options={filterOptions}
              onSelect={(option) => {
                setActiveRating({
                  value: option.value as (typeof filterOptions)[0]['value'],
                  label: option.label as string,
                });
                onFilter(option.value as (typeof filterOptions)[0]['value']);
              }}
              isDarkTheme={false}
              noSelectedOptionPlaceholder={microcopy.rating}
              selectedOption={
                activeRating && {
                  value: activeRating.value,
                  label: activeRating.label,
                }
              }
              label={microcopy.rating}
              className="max-w-10 text-sm [&_label]:font-normal [&_label]:text-grey-600"
            />
          </div>
          <div className="w-1/2 text-sm text-grey-600">
            <Dropdown
              id="sort-by-dropdown"
              options={sortByOptions}
              onSelect={(option) => {
                setActiveSortBy({
                  value: option.value as ProductReviewSortByValues,
                  label: option.label as string,
                });
                onSort(option.value as ProductReviewSortByValues);
              }}
              isDarkTheme={false}
              noSelectedOptionPlaceholder={microcopy.sortBy}
              selectedOption={
                activeSortBy && {
                  value: activeSortBy.value,
                  label: activeSortBy.label,
                }
              }
              label={microcopy.sortBy}
              className="[&_label]:font-normal [&_label]:text-grey-600"
            />
          </div>
        </div>
        <RichText
          className="mt-4 flex items-center text-sm text-grey-600 md:mt-0"
          html={
            hasMounted
              ? interpolateStringWithData(microcopy.results, {
                  start: reviews?.length ? start : 0,
                  end,
                  total,
                })
              : ''
          }
          type="p"
        />
      </nav>
      <div>
        {reviews?.length ? (
          reviews.map((review: UserProductReviewPropsData) => (
            <UserProductReview
              onDeleteReview={onDeleteReview}
              onEmailPublisher={onEmailPublisher}
              onSubmitReview={onSubmitReview}
              className="mb-4"
              key={review.reviewDetails.id}
              onSubmitReport={onSubmitReport}
              onEditReview={onEditReview}
              onEditReply={() => {
                if (review.reviewDetails.reply) {
                  onEditReply(review.reviewDetails.reply.id);
                }
              }}
              onDeleteReply={() => {
                if (review.reviewDetails.reply) {
                  onDeleteReply(review.reviewDetails.reply.id);
                }
              }}
              onSubmitReply={onSubmitReply}
              microcopy={{
                reviewerInfo: microcopy.reviewerInfo,
                reviewDetails: microcopy.reviewDetails,
              }}
              onHelpfulVotesReply={() => {
                if (review.reviewDetails.reply) {
                  onHelpfulVotesReply(review.reviewDetails.reply.id);
                }
              }}
              onHelpfulVotesReview={() =>
                onHelpfulVotesReview(review.reviewDetails.id)
              }
              {...review}
            />
          ))
        ) : (
          <div className="text-sm text-grey-600">{renderEmptyState()}</div>
        )}
      </div>
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          microcopy={microcopy.pagination}
          onPageChange={(page) => onPageChange(page)}
          className="mt-6 flex justify-center"
          maxVisiblePages={4}
        />
      )}
    </section>
  );
};
